"use client";

import { cx } from "@admin-ui/utils/cx";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { Bold01, Dotpoints01, Italic01, List, Strikethrough01, Underline01 } from "@untitledui/icons";
import { ToggleButton } from "react-aria-components";

const buttonStyles = cx(
  "flex size-8 items-center justify-center rounded-md text-secondary outline-hidden transition",
  "hover:bg-secondary hover:text-primary",
  "data-pressed:bg-secondary",
  "data-selected:bg-secondary data-selected:text-primary",
  "data-focus-visible:ring-2 data-focus-visible:ring-brand",
);

interface ToolbarProps {
  editor: Editor;
}

export function Toolbar({ editor }: ToolbarProps) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }: { editor: Editor }) => ({
      isBold: e.isActive("bold"),
      isItalic: e.isActive("italic"),
      isUnderline: e.isActive("underline"),
      isStrike: e.isActive("strike"),
      isBulletList: e.isActive("bulletList"),
      isOrderedList: e.isActive("orderedList"),
    }),
  });

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="flex items-center gap-0.5 border-b border-secondary bg-primary px-1 py-1"
    >
      <ToggleButton
        aria-label="Bold"
        className={buttonStyles}
        isSelected={state.isBold}
        onChange={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold01 className="size-4" aria-hidden />
      </ToggleButton>
      <ToggleButton
        aria-label="Italic"
        className={buttonStyles}
        isSelected={state.isItalic}
        onChange={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic01 className="size-4" aria-hidden />
      </ToggleButton>
      <ToggleButton
        aria-label="Underline"
        className={buttonStyles}
        isSelected={state.isUnderline}
        onChange={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline01 className="size-4" aria-hidden />
      </ToggleButton>
      <ToggleButton
        aria-label="Strikethrough"
        className={buttonStyles}
        isSelected={state.isStrike}
        onChange={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough01 className="size-4" aria-hidden />
      </ToggleButton>

      <div className="mx-0.5 h-5 w-px bg-secondary" />

      <ToggleButton
        aria-label="Bullet list"
        className={buttonStyles}
        isSelected={state.isBulletList}
        onChange={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="size-4" aria-hidden />
      </ToggleButton>
      <ToggleButton
        aria-label="Ordered list"
        className={buttonStyles}
        isSelected={state.isOrderedList}
        onChange={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <Dotpoints01 className="size-4" aria-hidden />
      </ToggleButton>
    </div>
  );
}
